import { CumulInterval, SalesReportDataOrigin, SalesReportDataType, SalesReportSettingsType, SalesReportToJSON } from '../SalesReport';
import { SalesReportDate } from "./SalesReportDate";
import { dateUtils } from "../utils/DateUtils";
import { SalesApi } from '../api/SalesApi';
import { isNull } from "lodash";
import { DateTime } from "luxon";
import { Preferences } from '@capacitor/preferences';
import { CurrencyFormatOptions, formatAmount, formatCurrency, formatPercentage } from '../utils/Languages';
import { IntervalPoint, PreviousIntervalPoint, ProductsReportResponse, SalesByClientTypesResponse, SalesByClientsResponse, SalesByIntervalResponse, TaxDataKey, MembersStatsResponse, MemberDimension, MemberMetric } from '../responses';

export class SalesReport {
    private _id: string = '';
    /**
     * The sales report card title
     */
    private _title: string = '';

    /**
     * The sales report type (daily, weekly, monthly, yearly)
     */
    private _type: SalesReportSettingsType = 'daily';

    /**
     * The sales report interval (hour, day, week, month, year)
     */
    private _interval: CumulInterval = 'hour';

    /**
     * The main (second by time) date of the report (the current date by default)
     */
    private _date: SalesReportDate;

    /**
     * The previous date of the report (the previous day by default)
     */
    private _previousDate?: SalesReportDate | null = null;

    /**
     * The default span between the current date and the previous date
     */
    private _periodBeforeDate: number = 1;

    /**
     * The data origin (web or all)
     */
    private _dataOrigin: SalesReportDataOrigin = 'all';

    /**
     * The data type (all or deposit)
     */
    private _dataType: SalesReportDataType = 'all';

    /**
     * The tax is included in the amount
     */
    private _taxIncluded: boolean = true;

    /**
     * Raw data of the reports
     */
    private _data: SalesByIntervalResponse | null = null;

    private _productsReport: ProductsReportResponse | null = null;

    private _clientTypesReport: SalesByClientTypesResponse | null = null;
    private _clientTypesAggregation: string | null = null;

    private _clientsReport: SalesByClientsResponse | null = null;
    private _clientsAggregation: string | null = null;

    private _membersReport: MembersStatsResponse | null = null;
    private _membersAggregation: string | null = null;

    private _membersConversionData: MembersStatsResponse | null = null;

    private _salesAggregation: string | null = null;

    private _loading: boolean = false;

    /**
     * The date of the last update of the data
     */
    private _updated_at: Date | null = null;

    private _centerId: number = 0

    private _inDashboard: boolean = false

    private _language: string = navigator.language

    private _currency: string = 'EUR'

    private _gmt: number = 0

    private _disconnected: boolean = false
    private _serverError: boolean = false

    /** In-flight promise deduplication */
    private _fetchAllPromise: Promise<void> | null = null
    private _fetchDataPromise: Promise<void> | null = null
    private _fetchProductsPromise: Promise<void> | null = null
    private _fetchClientTypesPromise: Promise<void> | null = null
    private _fetchClientsPromise: Promise<void> | null = null
    private _fetchMembersPromise: Promise<void> | null = null

    /**
     * Transient period offset for period navigation (not persisted)
     */
    private _periodOffset: number = 0

    private _customStartDate?: string
    private _customEndDate?: string

    private _previousCustomStartDate?: string
    private _previousCustomEndDate?: string

    public constructor() {
        // Initialize all the properties
        this.id = '';
        this.title = '';
        this.type = 'daily';
        this.interval = 'hour';
        this.previousDate = null;
        this.periodBeforeDate = 1;
        this.dataOrigin = 'all';
        this.taxIncluded = true;
        this.data = null;
        this.updated_at = null;
        this.centerId = undefined;
        this.gmt = 0;
        this._inDashboard = false;
        this._disconnected = false;
        this._serverError = false;

        this._date = new SalesReportDate(DateTime.now(), this.type, this.gmt);
    }

    get centerTime() {
        return DateTime.utc().setZone(this.getUtcString(), { keepLocalTime: false });
    }

    get gmt() {
        return this._gmt;
    }

    set gmt(gmt: number) {
        this._gmt = gmt;
    }

    get language() {
        return this._language;
    }

    set language(language: string) {
        this._language = language;
    }

    get currency() {
        return this._currency;
    }

    set currency(currency: string) {
        this._currency = currency;
    }

    get baseKey() {
        return `sales_report_${this.id}_${this.centerId}`
    }

    set inDashboard(inDashboard: boolean) {
        this._inDashboard = inDashboard;
    }

    get inDashboard() {
        return this._inDashboard;
    }

    get id() {
        return this._id ?? '';
    }

    set id(id: string) {
        this._id = id;
    }

    get title() {
        return this._title;
    }

    set title(title: string) {
        this._title = title;
    }

    get type() {
        return this._type;
    }

    set type(type: SalesReportSettingsType) {
        this._type = type;

        // Auto update the report type of the date and previous date
        this.date.reportType = type;
        if (type !== 'custom') {
            this.previousDate.reportType = type;
        }
    }

    get interval() {
        return this._interval;
    }

    set interval(interval: CumulInterval) {
        this._interval = interval;
    }

    set date(date: DateTime) {
        this._date = new SalesReportDate(date, this.type, this.gmt);
    }

    get date(): SalesReportDate {
        return this._date ? this._date : new SalesReportDate(DateTime.now(), this.type, this.gmt);
    }

    set previousDate(previousDate: Date | null) {
        if (isNull(previousDate)) {
            this._previousDate = previousDate;
            return;
        }
        this._previousDate = new SalesReportDate(DateTime.fromJSDate(previousDate), this.type, this.gmt);
    }

    get previousDate(): SalesReportDate {
        if (!this._previousDate) {
            return new SalesReportDate(
                dateUtils.getDefaultPreviousDate(this.type, this.periodBeforeDate, this.date.date),
                this.type,
                this.gmt
            )
        }

        return this._previousDate;
    }

    get periodBeforeDate() {
        return this._periodBeforeDate;
    }

    set periodBeforeDate(periodBeforeDate: number) {
        this._periodBeforeDate = periodBeforeDate;
    }

    get dataOrigin() {
        return this._dataOrigin;
    }

    set dataOrigin(dataOrigin: SalesReportDataOrigin) {
        this._dataOrigin = dataOrigin;
    }

    get dataType() {
        return this._dataType;
    }

    set dataType(dataType: SalesReportDataType) {
        this._dataType = dataType;
    }

    get taxIncluded() {
        return this._taxIncluded;
    }

    set taxIncluded(taxIncluded: boolean) {
        this._taxIncluded = taxIncluded;
    }

    set data(data: SalesByIntervalResponse | null) {
        this._data = data;
    }

    get data() {
        return this._data;
    }

    set loading(loading: boolean) {
        this._loading = loading;
    }

    get loading(): boolean {
        return this._loading;
    }

    get updated_at(): Date | null {
        return this._updated_at;
    }

    set updated_at(updated_at: Date | null) {
        this._updated_at = updated_at;
    }

    /**
     * Check if the data is still fresh (within TTL window)
     * @param ttlMs Time-to-live in milliseconds (default: 5 minutes)
     */
    isFresh(ttlMs: number = 300_000): boolean {
        if (!this._updated_at) return false;
        return (Date.now() - this._updated_at.getTime()) < ttlMs;
    }

    set centerId(centerId: number | undefined) {
        this._centerId = centerId ?? 0;
    }

    get centerId(): number | undefined {
        return this._centerId;
    }

    get productsReport(): ProductsReportResponse | null {
        return this._productsReport;
    }

    set productsReport(productsReport: ProductsReportResponse | null) {
        this._productsReport = productsReport;
    }

    get clientTypesReport(): SalesByClientTypesResponse | null {
        return this._clientTypesReport;
    }

    set clientTypesReport(report: SalesByClientTypesResponse | null) {
        this._clientTypesReport = report;
    }

    get clientTypesAggregation(): string | null {
        return this._clientTypesAggregation;
    }

    set clientTypesAggregation(agg: string | null) {
        this._clientTypesAggregation = agg;
    }

    get clientsReport(): SalesByClientsResponse | null {
        return this._clientsReport;
    }

    set clientsReport(report: SalesByClientsResponse | null) {
        this._clientsReport = report;
    }

    get clientsAggregation(): string | null {
        return this._clientsAggregation;
    }

    set clientsAggregation(agg: string | null) {
        this._clientsAggregation = agg;
    }

    get membersReport(): MembersStatsResponse | null {
        return this._membersReport;
    }

    set membersReport(report: MembersStatsResponse | null) {
        this._membersReport = report;
    }

    get membersAggregation(): string | null {
        return this._membersAggregation;
    }

    set membersAggregation(agg: string | null) {
        this._membersAggregation = agg;
    }

    get membersConversionData(): MembersStatsResponse | null {
        return this._membersConversionData;
    }

    set membersConversionData(data: MembersStatsResponse | null) {
        this._membersConversionData = data;
    }

    get salesAggregation(): string | null {
        return this._salesAggregation;
    }

    set salesAggregation(agg: string | null) {
        this._salesAggregation = agg;
    }

    get disconnected(): boolean {
        return this._disconnected;
    }

    set disconnected(disconnected: boolean) {
        this._disconnected = disconnected;
    }

    get customStartDate(): string | undefined {
        return this._customStartDate;
    }

    set customStartDate(date: string | undefined) {
        this._customStartDate = date;
        if (date) {
            this._date.customStart = DateTime.fromISO(date);
        } else {
            this._date.customStart = undefined;
        }
    }

    get customEndDate(): string | undefined {
        return this._customEndDate;
    }

    set customEndDate(date: string | undefined) {
        this._customEndDate = date;
        if (date) {
            this._date.customEnd = DateTime.fromISO(date);
        } else {
            this._date.customEnd = undefined;
        }
    }

    get previousCustomStartDate(): string | undefined {
        return this._previousCustomStartDate;
    }

    set previousCustomStartDate(date: string | undefined) {
        this._previousCustomStartDate = date;
        if (date) {
            if (!this._previousDate) {
                this._previousDate = new SalesReportDate(DateTime.fromISO(date), this.type, this.gmt);
            }
            this._previousDate.reportType = 'custom';
            this._previousDate.customStart = DateTime.fromISO(date);
        } else {
            if (this._previousDate) {
                this._previousDate.customStart = undefined;
            }
        }
    }

    get previousCustomEndDate(): string | undefined {
        return this._previousCustomEndDate;
    }

    set previousCustomEndDate(date: string | undefined) {
        this._previousCustomEndDate = date;
        if (date) {
            if (!this._previousDate) {
                this._previousDate = new SalesReportDate(DateTime.fromISO(date), this.type, this.gmt);
            }
            this._previousDate.reportType = 'custom';
            this._previousDate.customEnd = DateTime.fromISO(date);
        } else {
            if (this._previousDate) {
                this._previousDate.customEnd = undefined;
            }
        }
    }

    static fromSelfJson(json: any): SalesReport {
        const report = new SalesReport();
        report.id = json.id ?? 0;
        report.title = json.title;
        report.type = json.type;
        report.interval = json.interval;
        report.periodBeforeDate = json.periodBeforeDate;
        report.dataOrigin = json.dataOrigin;
        report.dataType = json.dataType;
        report.taxIncluded = json.taxIncluded;
        report.data = json.data;

        if (json.customStartDate) {
            report.customStartDate = json.customStartDate;
        }
        if (json.customEndDate) {
            report.customEndDate = json.customEndDate;
        }
        if (json.previousCustomStartDate) {
            report.previousCustomStartDate = json.previousCustomStartDate;
        }
        if (json.previousCustomEndDate) {
            report.previousCustomEndDate = json.previousCustomEndDate;
        }

        return report;
    }

    updateFromJson(json: any): SalesReport {
        this.id = json.id ?? "";
        this.title = json.title;
        this.type = json.type;
        this.interval = json.interval;
        this.periodBeforeDate = json.periodBeforeDate;
        this.dataOrigin = json.dataOrigin;
        this.dataType = json.dataType;
        this.taxIncluded = json.taxIncluded;
        this.data = json.data;

        if (json.customStartDate) {
            this.customStartDate = json.customStartDate;
        }
        if (json.customEndDate) {
            this.customEndDate = json.customEndDate;
        }
        if (json.previousCustomStartDate) {
            this.previousCustomStartDate = json.previousCustomStartDate;
        }
        if (json.previousCustomEndDate) {
            this.previousCustomEndDate = json.previousCustomEndDate;
        }

        return this;
    }

    updateLanguageAndCurrency(language: string, currency: string) {
        this.language = language;
        this.currency = currency;
    }

    setGmt(gmt: number) {
        this.gmt = gmt;
        this.date.offset = gmt;
        this.previousDate.offset = gmt;
    }

    private getUtcString(): string {
        if (this.gmt >= 0) {
            return `UTC+${this.gmt}`
        } else if (this.gmt < 0) {
            return `UTC${this.gmt}`
        } else {
            return `UTC${this.gmt}`
        }
    }

    toJSON(): SalesReportToJSON {
        return {
            id: this.id,
            title: this.title,
            type: this.type,
            interval: this.interval,
            periodBeforeDate: this.periodBeforeDate,
            dataOrigin: this.dataOrigin,
            dataType: this.dataType,
            taxIncluded: this.taxIncluded,
            language: this.language,
            currency: this.currency,
            ...(this.type === 'custom' && this._customStartDate ? { customStartDate: this._customStartDate } : {}),
            ...(this.type === 'custom' && this._customEndDate ? { customEndDate: this._customEndDate } : {}),
            ...(this.type === 'custom' && this._previousCustomStartDate ? { previousCustomStartDate: this._previousCustomStartDate } : {}),
            ...(this.type === 'custom' && this._previousCustomEndDate ? { previousCustomEndDate: this._previousCustomEndDate } : {}),
        }
    }

    getDateAsLabel(date: Date): string {
        switch (this.type) {
            case 'daily':
                return dateUtils.formatDay(date);
            case 'weekly':
                return dateUtils.formatWeek(date);
            case 'monthly':
                return dateUtils.formatMonth(date);
            case 'yearly':
                return dateUtils.formatYear(date);
            default:
                return dateUtils.formatDay(date);
        }
    }

    setDate(date: DateTime) {
        this.date = date;
    }

    setPreviousDate(date: Date) {
        this.previousDate = date;
    }

    resetDates() {
        this.date = DateTime.now()
        this.previousDate = null;
    }

    resetPreviousDate() {
        this.previousDate = null;
    }

    async fetchAll(centerId: number, force: boolean = false) {
        if (centerId !== undefined) {
            this.centerId = centerId;
        }

        // Skip fetch if data is fresh and not forced
        if (!force && this.isFresh() && this.data && this.productsReport) {
            this.loading = false;
            return;
        }

        // Deduplicate: reuse in-flight fetchAll if not forced
        if (this._fetchAllPromise && !force) {
            return this._fetchAllPromise;
        }

        // Don't attempt to fetch if already in error state
        if ((this.disconnected || this.serverError) && !force) {
            this.loading = false;
            return;
        }

        this.loading = true;

        const promise = (async () => {
            try {
                await Promise.all([
                    this.fetchData(centerId, force),
                    this.fetchProductsReport()
                ]);

                this.updated_at = new Date();
            } catch (error) {
                // Error handling is done in individual fetch methods
                console.error('Failed to fetch sales report data:', error);
            } finally {
                this.loading = false;
                this._fetchAllPromise = null;
            }
        })();

        this._fetchAllPromise = promise;
        return promise;
    }

    async fetchData(centerId: number, force: boolean = false, intervalOverride?: string) {
        // Skip fetch if data is fresh, not forced, and same aggregation
        if (!force && this.isFresh() && this.data && (!intervalOverride || intervalOverride === this.salesAggregation)) {
            return;
        }

        // Deduplicate: reuse in-flight request if not forced and same interval
        if (this._fetchDataPromise && !force && (!intervalOverride || intervalOverride === this.salesAggregation)) {
            return this._fetchDataPromise;
        }

        const promise = (async () => {
            // Call the API to fetch the data
            const deposit = this.dataType === 'deposit';
            this.loading = true;
            const effectiveInterval = (intervalOverride ?? this.interval) as any;

            try {
                const resp = await SalesApi.fetchSalesByInterval(
                    this.date.startDate, this.date.endDate,
                    effectiveInterval, this.dataOrigin, deposit, centerId,
                    this.previousDate.startDate, this.previousDate.endDate
                )
                this.data = resp;
                this.salesAggregation = effectiveInterval as string;

                // If successful, clear error states
                if (this.disconnected) {
                    this.markAsConnected();
                }
                if (this.serverError) {
                    this.clearServerError();
                }
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    this.markAsDisconnected();
                    this.loading = false;
                    const { useSalesReportsStore } = await import('../stores/salesReports');
                    const salesReportsStore = useSalesReportsStore();
                    salesReportsStore.markCenterAsDisconnected(centerId);
                } else if (status && status >= 500) {
                    this.markAsServerError();
                    this.loading = false;
                }
                throw error;
            } finally {
                this._fetchDataPromise = null;
            }

            this.loading = false;
        })();

        this._fetchDataPromise = promise;
        return promise;
    }

    async fetchProductsReport() {
        if (!this.centerId) {
            throw new Error('Unable to fetch products report without a center ID');
        }

        // Deduplicate: reuse in-flight request
        if (this._fetchProductsPromise) {
            return this._fetchProductsPromise;
        }

        const centerId = this.centerId!;
        const promise = (async () => {
            const deposit = this.dataType === 'deposit';

            try {
                const resp = await SalesApi.fetchProductsReport(
                    this.date.startDate, this.date.endDate,
                    this.interval, this.dataOrigin, deposit, centerId,
                    this.previousDate.startDate, this.previousDate.endDate
                );

                this.productsReport = resp;

                // If successful, clear error states
                if (this.disconnected) {
                    this.markAsConnected();
                }
                if (this.serverError) {
                    this.clearServerError();
                }
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    this.markAsDisconnected();
                    const { useSalesReportsStore } = await import('../stores/salesReports');
                    const salesReportsStore = useSalesReportsStore();
                    salesReportsStore.markCenterAsDisconnected(centerId);
                } else if (status && status >= 500) {
                    this.markAsServerError();
                }
                throw error;
            } finally {
                this._fetchProductsPromise = null;
            }
        })();

        this._fetchProductsPromise = promise;
        return promise;
    }


    async fetchClientTypesReport(intervalOverride?: string) {
        if (!this.centerId) {
            throw new Error('Unable to fetch client types report without a center ID');
        }

        // Skip if data exists and aggregation hasn't changed
        if (this._clientTypesReport && (!intervalOverride || intervalOverride === this._clientTypesAggregation)) {
            return;
        }

        // Deduplicate concurrent calls
        if (this._fetchClientTypesPromise) {
            return this._fetchClientTypesPromise;
        }

        const centerId = this.centerId;
        const promise = (async () => {
            try {
                const deposit = this.dataType === 'deposit';
                const resp = await SalesApi.fetchSalesByClientTypes(
                    this.date.startDate, this.date.endDate,
                    (intervalOverride ?? this.interval) as any, this.dataOrigin, deposit, centerId,
                    this.previousDate.startDate, this.previousDate.endDate
                );

                this.clientTypesReport = resp;
                this.clientTypesAggregation = (intervalOverride ?? this.interval) as string;

                if (this.disconnected) {
                    this.markAsConnected();
                }
                if (this.serverError) {
                    this.clearServerError();
                }
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    this.markAsDisconnected();
                    const { useSalesReportsStore } = await import('../stores/salesReports');
                    const salesReportsStore = useSalesReportsStore();
                    salesReportsStore.markCenterAsDisconnected(centerId);
                } else if (status && status >= 500) {
                    this.markAsServerError();
                }
                throw error;
            } finally {
                this._fetchClientTypesPromise = null;
            }
        })();

        this._fetchClientTypesPromise = promise;
        return promise;
    }

    async fetchClientsReport(intervalOverride?: string) {
        if (!this.centerId) {
            throw new Error('Unable to fetch clients report without a center ID');
        }

        // Skip if data exists and aggregation hasn't changed
        if (this._clientsReport && (!intervalOverride || intervalOverride === this._clientsAggregation)) {
            return;
        }

        // Deduplicate concurrent calls
        if (this._fetchClientsPromise) {
            return this._fetchClientsPromise;
        }

        const centerId = this.centerId;
        const promise = (async () => {
            try {
                const deposit = this.dataType === 'deposit';
                const resp = await SalesApi.fetchSalesByClients(
                    this.date.startDate, this.date.endDate,
                    (intervalOverride ?? this.interval) as any, this.dataOrigin, deposit, centerId,
                    this.previousDate.startDate, this.previousDate.endDate
                );

                this.clientsReport = resp;
                this.clientsAggregation = (intervalOverride ?? this.interval) as string;

                if (this.disconnected) {
                    this.markAsConnected();
                }
                if (this.serverError) {
                    this.clearServerError();
                }
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    this.markAsDisconnected();
                    const { useSalesReportsStore } = await import('../stores/salesReports');
                    const salesReportsStore = useSalesReportsStore();
                    salesReportsStore.markCenterAsDisconnected(centerId);
                } else if (status && status >= 500) {
                    this.markAsServerError();
                }
                throw error;
            } finally {
                this._fetchClientsPromise = null;
            }
        })();

        this._fetchClientsPromise = promise;
        return promise;
    }

    async fetchMembersReport(
        dimensions: MemberDimension[],
        metrics: MemberMetric[],
        intervalOverride?: string
    ) {
        if (!this.centerId) {
            throw new Error('Unable to fetch members report without a center ID');
        }

        // Skip if data exists and aggregation hasn't changed
        if (this._membersReport && (!intervalOverride || intervalOverride === this._membersAggregation)) {
            return;
        }

        // Deduplicate concurrent calls
        if (this._fetchMembersPromise) {
            return this._fetchMembersPromise;
        }

        const centerId = this.centerId;
        const promise = (async () => {
            try {
                const { MembersApi } = await import('../api/MembersApi');
                const resp = await MembersApi.fetchMembersStats(
                    this.date.startDate, this.date.endDate,
                    (intervalOverride ?? this.interval) as any, centerId,
                    dimensions, metrics,
                    this.previousDate?.startDate, this.previousDate?.endDate
                );

                this.membersReport = resp;
                this.membersAggregation = (intervalOverride ?? this.interval) as string;

                if (this.disconnected) {
                    this.markAsConnected();
                }
                if (this.serverError) {
                    this.clearServerError();
                }
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    this.markAsDisconnected();
                    const { useSalesReportsStore } = await import('../stores/salesReports');
                    const salesReportsStore = useSalesReportsStore();
                    salesReportsStore.markCenterAsDisconnected(centerId);
                } else if (status && status >= 500) {
                    this.markAsServerError();
                }
                throw error;
            } finally {
                this._fetchMembersPromise = null;
            }
        })();

        this._fetchMembersPromise = promise;
        return promise;
    }

    /**
     * Get the data from the preferences (if it exists and is not expired)
     * 
     * @param previous 
     * @param expiry (in minutes)
     * @returns 
     */
    async getDataFromPreferences(previous: boolean, expiry?: number): Promise<SalesByIntervalResponse | undefined> {
        if (!expiry) { expiry = 15 };
        const lastUpdated = await Preferences.get({ key: `${this.baseKey}.updated_at` });
        // Check if the data has a last updated date
        if (lastUpdated.value === null) return;

        // If the data is expired, return
        const expiresAt = DateTime.fromJSDate(new Date(JSON.parse(lastUpdated.value))).plus({ minutes: expiry });
        if (expiresAt < DateTime.now()) return;

        // Get the data from the preferences
        const key = previous ? `${this.baseKey}.previous` : `${this.baseKey}.current`;
        let storedData = await Preferences.get({ key: key })

        // If the data is stored, return it
        if (storedData.value) {
            return JSON.parse(storedData.value);
        }

        // If the data is not stored, return
        return
    }

    /**
     * Set the data in the preferences
     * 
     * @param data 
     * @param previous 
     */
    async setDataInPreferences(data: SalesByIntervalResponse, previous: boolean) {
        const key = previous ? `${this.baseKey}.previous` : `${this.baseKey}.current`;
        await this.removeDataInPreferences(previous);
        Preferences.set({ key: key, value: JSON.stringify(data) });
        Preferences.set({ key: `${this.baseKey}.updated_at`, value: JSON.stringify(this.updated_at) });
    }

    async removeDataInPreferences(previous?: boolean) {
        if (previous === undefined) {
            await this.removeDataInPreferences(true)
            await this.removeDataInPreferences(false)
        }

        const key = previous ? `${this.baseKey}.previous` : `${this.baseKey}.current`;
        await Preferences.remove({ key: key });
        await Preferences.remove({ key: `${this.baseKey}.updated_at` });
    }


    getLiveCenterDatetime(): DateTime {
        let t = DateTime.now();
        return t.setZone(this.getUtcString(), { keepLocalTime: false });
    }

    getBoundaries(): string[] {
        let boundaries = [
            this.date.formatBoundaries()
        ];

        if (this.data?.previous_intervals) {
            boundaries = [
                this.previousDate.formatBoundaries(),
                ...boundaries
            ]
        }

        return boundaries;
    }

    async reload(centerID: number) {
        this.resetDates();
        this.resetPreviousDate();
        this.data = null;
        this.clientTypesReport = null;
        this.clientsReport = null;
        this.membersReport = null;
        this.membersConversionData = null;
        this.loading = false;
        this.updated_at = null;

        await this.fetchData(centerID, true)

    }


    getTaxKey(): TaxDataKey {
        return this.taxIncluded ? 'included_tax' : 'excluded_tax';
    }

    getCurrentTotal(): number {
        const intervals = this.data?.intervals ?? [];
        if (intervals.length === 0) return 0;
        let lastKey = intervals.length - 1;
        return intervals[lastKey].data.cumulative.tax_data[this.getTaxKey()] || 0
    }

    getCurrentTotalFormatted(): string {
        return formatCurrency(this.getCurrentTotal(), this.language, this.currency)
    }

    getPreviousTotal(): number {
        if (!this.data?.previous_intervals) { return 0 }
        let lastKey = this.data?.previous_intervals.length - 1;
        return this.data?.previous_intervals[lastKey].data.cumulative.tax_data[this.getTaxKey()] || 0
    }

    getPreviousTotalFormatted(): string {
        return formatCurrency(this.getPreviousTotal(), this.language, this.currency)
    }

    getComparablePreviousTotal(): number {
        if (!this.data || !this.data.previous_intervals) return 0;

        const nbKeys = this.data?.intervals.length;

        // If no current data is given, show the whole result of the previous period
        if (nbKeys === 0) {
            return this.getPreviousTotal();
        }

        if (!nbKeys) return 0;

        return this.data?.previous_intervals[nbKeys - 1]?.data.cumulative.tax_data[this.getTaxKey()] ?? 0
    }

    getPreviousCumulatedIntervals() {
        return this.data?.previous_intervals?.map((interval: PreviousIntervalPoint) =>
            interval.data.cumulative.tax_data[this.getTaxKey()]
        ) || []
    }

    getPreviousIntervals() {
        return this.data?.previous_intervals?.map((interval: PreviousIntervalPoint) =>
            interval.data.period.tax_data[this.getTaxKey()]
        ) || []

    }

    getCurrentCumulatedIntervals() {
        let intervals = this.data?.intervals.map((interval: IntervalPoint) =>
            interval.data.cumulative.tax_data[this.getTaxKey()]
        ) || []

        // Find the last index where values differ, removing duplicates from the end
        let lastDifferentIndex = intervals.length - 1;

        for (let i = intervals.length - 2; i >= 0; i--) {
            if (intervals[i] !== intervals[lastDifferentIndex]) {
                lastDifferentIndex = i + 1;
                break;
            }
            if (i === 0) {
                lastDifferentIndex = 0;
            }
        }

        return intervals.slice(0, lastDifferentIndex + 1);
    }

    getCurrentIntervals() {
        return this.data?.intervals?.map((interval: IntervalPoint) =>
            interval.data.period.tax_data[this.getTaxKey()]
        ) || []
    }

    // getComparableDifference(): number {
    // }

    getEquitableTotal(): number {

        if (!this.data?.previous_intervals) return 0;

        let lastIdx = this.getCurrentEndingIndex();
        return this.data.previous_intervals[lastIdx]?.data.cumulative.tax_data[this.getTaxKey()] ?? 0
    }

    getEquitableTotalFormatted(): string {
        const formatOptions: CurrencyFormatOptions = {
            language: this.language,
            currency: this.currency
        }

        return formatAmount(this.getEquitableTotal(), formatOptions)
    }

    getDifferencePercentage(): number {
        const current = this.getCurrentTotal();
        const equitable = this.getEquitableTotal();

        return (current - equitable) / equitable;
    }

    getDifferencePercentageFormatted(): string {
        const percentage = this.getDifferencePercentage();
        return formatPercentage(percentage, this.language)
    }

    // Méthodes pour le panier moyen
    getCurrentAverageBasket(): number {
        const totalPaid = this.data?.summary.total_period.total_paid ?? 0;
        const count = this.data?.summary.total_period.total_documents ?? 0;
        return totalPaid / count;
    }

    getCurrentAverageBasketFormatted(): string {
        return formatCurrency(this.getCurrentAverageBasket(), this.language, this.currency, true);
    }

    getPreviousAverageBasket(): number {
        const totalPaid = this.data?.summary.total_previous_period?.total_paid ?? 0;
        const count = this.data?.summary.total_previous_period?.total_documents ?? 0;
        return count > 0 ? totalPaid / count : 0;
    }

    getPreviousAverageBasketFormatted(): string {
        return formatCurrency(this.getPreviousAverageBasket(), this.language, this.currency, true);
    }

    getEquitableAverageBasket(): number {
        const totalPaid = this.data?.summary.total_equitable_period?.total_paid ?? 0;
        const count = this.data?.summary.total_equitable_period?.total_documents ?? 0;
        return count > 0 ? totalPaid / count : 0;
    }

    getAverageBasketVariation(): number {
        const current = this.getCurrentAverageBasket();
        const equitable = this.getEquitableAverageBasket();

        if (equitable === 0) return 0;
        return (current - equitable) / equitable;
    }

    getAverageBasketVariationPercentage(): number {
        return this.getAverageBasketVariation() * 100;
    }

    getAverageBasketVariationFormatted(): string {
        const variation = this.getAverageBasketVariationPercentage();
        const sign = variation >= 0 ? '+' : '';
        return `${sign}${variation.toFixed(1)}%`;
    }

    get serverError(): boolean {
        return this._serverError;
    }

    get periodOffset(): number {
        return this._periodOffset;
    }

    set periodOffset(offset: number) {
        this._periodOffset = offset;
    }

    markAsDisconnected(): void {
        this._disconnected = true;
    }

    markAsConnected(): void {
        this._disconnected = false;
    }

    markAsServerError(): void {
        this._serverError = true;
    }

    clearServerError(): void {
        this._serverError = false;
    }

    // Méthodes pour les données de graphique du panier moyen
    getCurrentAverageBasketIntervals(): number[] {
        return this.data?.intervals.map((interval: IntervalPoint) => {
            const totalPaid = interval.data.period.total_paid;
            const count = interval.data.period.total_documents;
            return count > 0 ? totalPaid / count : 0;
        }) ?? [];
    }

    getPreviousAverageBasketIntervals(): number[] {
        return this.data?.previous_intervals?.map((interval: PreviousIntervalPoint) => {
            const totalPaid = interval.data.period.total_paid;
            const count = interval.data.period.total_documents;
            return count > 0 ? totalPaid / count : 0;
        }) ?? [];
    }

    getStartingIndex(current: boolean | undefined): number {
        let dataset = current ? this.data?.intervals : this.data?.previous_intervals;
        if (!dataset) {
            return 0
        }

        // get the first key that is not a zero
        for (let i = 0; i < dataset.length; i++) {
            const value = dataset[i].data.period.tax_data[this.getTaxKey()];
            if (value !== 0) {
                return i;
            }
        }

        return 0;
    }

    getEndingIndex(current: boolean | undefined): number {
        let dataset = current ? this.data?.intervals : this.data?.previous_intervals;
        if (!dataset) {
            return 0
        }

        // get the last key that is not a zero
        for (let i = dataset.length - 1; i >= 0; i--) {
            const value = dataset[i].data.period.tax_data[this.getTaxKey()];
            if (value !== 0) {
                return i;
            }
        }

        return 0;
    }

    getCurrentStartingIndex(): number {
        return this.getStartingIndex(true);
    }

    getPreviousStartingIndex(): number {
        return this.getStartingIndex(false);
    }

    getCurrentEndingIndex(): number {
        return this.getEndingIndex(true);
    }

    getPreviousEndingIndex(): number {
        return this.getEndingIndex(false);
    }

    getIndexRange(current: boolean | undefined): { start: number, end: number } {
        return {
            start: this.getStartingIndex(current),
            end: this.getEndingIndex(current)
        };
    }


    // getTotalAmountFormattedCurrency(type: boolean): string {
    // }

    // getMissingAmount(): number {
    // }

    // getFilledAmountPercentage(): number {
    // }

    // getMissingAmountPercentage(): number {
    // }

    // missingAmountPercentageFormatted(): string {
    // }

    // getEvolutionPercentage(): number {

    // }


    // getPreviousTotalAmount(): number {
    // }

    // getCurrentTotalAmount(): number {
    // }


    // getTotalAmountFormatted(type: boolean): string {
    //     let amount = type ? this.getPreviousTotalAmount() : this.getCurrentTotalAmount();
    //     return new Intl.NumberFormat(getLanguageCode(this.language), {
    //         style: 'decimal',
    //         minimumFractionDigits: 0,
    //         maximumFractionDigits: 0,
    //     }).format(amount);
    // }


    // getEvolutionPercentageFormatted(): string {
    //     const percentage = this.getEvolutionPercentage();
    //     return new Intl.NumberFormat(getLanguageCode(this.language), { style: 'percent' }).format(percentage / 100);
    // }
}